import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../../../api/axios";
import CustomModal from "../../../../components/ui/modal/modal.component";
import CustomDatePicker from "../../../../components/ui/customDatePicker/customDatePicker.component";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { CounterDetailsTable } from "../../../../components/settlements/settlementCreator/counterDetailsTable.component";
import creatorStyles from "../../../../components/settlements/settlementCreator/settlementCreator.module.css";
import EquipmentCounterForm from "../../../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "../../../../components/ui/equipmentCounterForm/lowCounterValueWarning.component";
import { useHasPermission } from "../../../../contexts/auth";
import {
  formatCounterInputValue,
  formatCounterProvider,
  formatCounterValue,
  formatDateTime,
  normalizeCounterInputValue,
} from "../../../../utils";
import {
  EQUIPMENT_COUNTERS_ENDPOINT,
  EQUIPMENT_COUNTERS_VERIFY_ENDPOINT,
  ERROR_SNACKBAR,
  SETTLEMENTS_ENDPOINT,
  SETTLEMENT_STATUS_TO_COLOR,
  SUCCESS_SNACKBAR,
} from "../../../../consts";

const COUNTER_KIND_LABELS: Record<string, string> = {
  BP: "Black pages",
  CP: "Color pages",
  SP: "Scanned pages",
};

interface CounterDetail {
  id: string;
  equipment_id?: string | number | null;
  kind: string;
  kind_display?: string;
  value?: string;
  verified_at?: string | null;
  read_at?: string;
  provider?: string;
  provider_display?: string;
  email_source_display?: string | null;
}

interface SettlementDetailPosition {
  id: string | number;
  counter: string | number | null;
  counter_detail: CounterDetail | null;
  previous_counter: string | number | null;
  previous_counter_detail: CounterDetail | null;
  // Falls back to the price's start_counter when there's no previous
  // settlement yet - always present, unlike previous_counter_detail.
  // Mirrors the fallback used by the sale-summary selector so both
  // views agree instead of one silently showing nothing.
  effective_previous_counter_detail: CounterDetail | null;
  investigation_reasons: string[];
}

interface SettlementDetailEquipment {
  id: string | number;
  full_name?: string;
}

interface SettlementDetail {
  id: string | number;
  status: string;
  status_display: string;
  sale_date?: string | null;
  total_overridden?: number | string | null;
  positions?: SettlementDetailPosition[];
  contract_term?: {
    client_name?: string | null;
    contract_info?: { number?: string } | null;
    equipments?: SettlementDetailEquipment[];
  };
}

interface SettlementActionModalProps {
  settlementId: number | string | null;
  opened: boolean;
  setOpened: (value: boolean) => void;
}

interface CounterInputRow {
  key: string;
  kind: string;
  equipmentId: string | number | undefined;
  removable: boolean;
  previousValue?: number;
  previousDate?: string;
}

export const SettlementActionModal = ({
  settlementId,
  opened,
  setOpened,
}: SettlementActionModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);

  const [settlement, setSettlement] = useState<SettlementDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const [editingCounterId, setEditingCounterId] = useState<string | null>(null);

  const [counterValues, setCounterValues] = useState<Record<string, string>>({});
  const [adHocRows, setAdHocRows] = useState<CounterInputRow[]>([]);
  const adHocIdRef = useRef(0);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [saleDate, setSaleDate] = useState("");
  const [totalOverridden, setTotalOverridden] = useState("");
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const fetchSettlement = useCallback(async () => {
    if (!settlementId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${SETTLEMENTS_ENDPOINT}${settlementId}/`);
      const data: SettlementDetail = response.data;
      setSettlement(data);
      setSaleDate(data.sale_date || dayjs().format("YYYY-MM-DD"));
      setTotalOverridden(
        data.total_overridden !== null && data.total_overridden !== undefined
          ? String(data.total_overridden)
          : ""
      );
    } catch (err) {
      setError(t("Failed to load settlement"));
    } finally {
      setLoading(false);
    }
  }, [settlementId, t]);

  useEffect(() => {
    if (opened) {
      setDirty(false);
      setAdHocRows([]);
      fetchSettlement();
    } else {
      setSettlement(null);
      setCounterValues({});
      setError(null);
    }
  }, [opened, settlementId, fetchSettlement]);

  const handleModalSetOpened = (value: boolean) => {
    if (!value && dirty) {
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("equipmentCounterAdded"));
    }
    setOpened(value);
  };

  const positions = settlement?.positions || [];
  const equipments = settlement?.contract_term?.equipments || [];
  const fallbackEquipmentId = equipments.length === 1 ? equipments[0].id : undefined;
  const hasMultipleEquipments = equipments.length > 1;

  const counterActionPositions = positions.filter(
    (position) => !position.counter || (position.investigation_reasons || []).includes("O")
  );
  const unverifiedPositions = positions.filter(
    (position) => position.counter && (position.investigation_reasons || []).includes("NV")
  );
  const readyToConfirm =
    settlement?.status === "W" &&
    positions.length > 0 &&
    positions.every((position) => position.counter) &&
    !positions.some((position) => (position.investigation_reasons || []).length > 0);

  const getEquipmentLabelById = (equipmentId?: string | number | null): string => {
    const match =
      equipmentId != null
        ? equipments.find((equipment) => String(equipment.id) === String(equipmentId))
        : undefined;
    return match?.full_name || t("Equipment");
  };

  const getPositionEquipmentId = (position: SettlementDetailPosition) =>
    position.counter_detail?.equipment_id ??
    position.effective_previous_counter_detail?.equipment_id ??
    fallbackEquipmentId;

  const getCounterKindLabel = (kind: string): string => t(COUNTER_KIND_LABELS[kind] || kind);

  const positionInputRows: CounterInputRow[] = counterActionPositions.map((position) => {
    const previous = position.effective_previous_counter_detail;
    return {
      key: String(position.id),
      kind: previous?.kind || position.counter_detail?.kind || "",
      equipmentId: getPositionEquipmentId(position),
      removable: false,
      previousValue:
        previous?.value !== undefined && previous?.value !== null
          ? Number(previous.value)
          : undefined,
      previousDate: previous?.read_at ? formatDateTime(previous.read_at) : undefined,
    };
  });
  const allInputRows: CounterInputRow[] = [...positionInputRows, ...adHocRows];

  // Counters are only ever addressed from this modal once a settlement has
  // actually flagged NEEDS_INVESTIGATION - a WAITING_FOR_CONFIRMATION
  // settlement is expected to already have everything it needs (Scan
  // included), so surfacing counter inputs there would be misleading.
  const canManageCounters = settlement?.status !== "W";

  const equipmentsNeedingScanButton = canManageCounters
    ? equipments.filter(
        (equipment) =>
          !allInputRows.some(
            (row) => row.kind === "SP" && String(row.equipmentId) === String(equipment.id)
          )
      )
    : [];

  const addScanRow = (equipmentId: string | number) => {
    adHocIdRef.current += 1;
    setAdHocRows((prev) => [
      ...prev,
      { key: `scan-${equipmentId}-${adHocIdRef.current}`, kind: "SP", equipmentId, removable: true },
    ]);
  };

  const removeAdHocRow = (key: string) => {
    setAdHocRows((prev) => prev.filter((row) => row.key !== key));
    setCounterValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const rowFieldLabel = (row: CounterInputRow) =>
    hasMultipleEquipments
      ? `${getEquipmentLabelById(row.equipmentId)} — ${getCounterKindLabel(row.kind)}`
      : getCounterKindLabel(row.kind);

  const previousCounterRows = positions.map((position) => {
    const previous = position.effective_previous_counter_detail;
    const current = position.counter_detail;
    const currentValue = current ? Number(current.value) : NaN;
    const previousValue = previous ? Number(previous.value) : NaN;
    const hasDifference = !Number.isNaN(currentValue) && !Number.isNaN(previousValue);

    return {
      id: current?.id,
      kind:
        current?.kind_display ||
        previous?.kind_display ||
        getCounterKindLabel((current?.kind || previous?.kind) as string),
      value: formatCounterValue(current?.value),
      previousValue: formatCounterValue(previous?.value),
      difference: hasDifference ? formatCounterValue(currentValue - previousValue) : "-",
      previousReadDate: previous?.read_at ? formatDateTime(previous.read_at) : "-",
      currentReadDate: current?.read_at ? formatDateTime(current.read_at) : "-",
      previousProvider: formatCounterProvider(
        previous?.provider_display,
        previous?.email_source_display
      ),
      previousProviderCode: previous?.provider || "-",
      currentProvider: formatCounterProvider(
        current?.provider_display,
        current?.email_source_display
      ),
      currentProviderCode: current?.provider || "-",
    };
  });

  const unverifiedRows = unverifiedPositions.map((position) => {
    const counter = position.counter_detail as CounterDetail;
    const previous = position.effective_previous_counter_detail;
    const currentValue = Number(counter.value);
    const previousValue = previous ? Number(previous.value) : NaN;
    const hasDifference = !Number.isNaN(currentValue) && !Number.isNaN(previousValue);

    return {
      id: counter.id,
      kind: counter.kind_display || getCounterKindLabel(counter.kind),
      value: formatCounterValue(counter.value),
      previousValue: formatCounterValue(previous?.value),
      difference: hasDifference ? formatCounterValue(currentValue - previousValue) : "-",
      previousReadDate: previous?.read_at ? formatDateTime(previous.read_at) : "-",
      currentReadDate: counter.read_at ? formatDateTime(counter.read_at) : "-",
      previousProvider: formatCounterProvider(
        previous?.provider_display,
        previous?.email_source_display
      ),
      previousProviderCode: previous?.provider || "-",
      currentProvider: formatCounterProvider(counter.provider_display, counter.email_source_display),
      currentProviderCode: counter.provider || "-",
    };
  });

  const getRowsToSubmit = () =>
    allInputRows.filter((row) => (counterValues[row.key] || "").trim() !== "");

  const performBulkSaveCounters = async (rowsToSubmit: CounterInputRow[]) => {
    setBulkSubmitting(true);
    try {
      for (const row of rowsToSubmit) {
        await axios.post(EQUIPMENT_COUNTERS_ENDPOINT, {
          equipment_id: row.equipmentId,
          provider: "U",
          read_at: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
          kind: row.kind,
          value: counterValues[row.key],
          verify: true,
        });
      }
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/refresh-calculation/`);
      enqueueSnackbar(t("Counter values saved and verified"), SUCCESS_SNACKBAR);
      setCounterValues((prev) => {
        const next = { ...prev };
        rowsToSubmit.forEach((row) => delete next[row.key]);
        return next;
      });
      setAdHocRows((prev) => prev.filter((row) => !rowsToSubmit.some((r) => r.key === row.key)));
      setDirty(true);
      await fetchSettlement();
    } catch (err: any) {
      const message = err?.response?.data?.message || t("Failed to add counter value");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkSaveCounters = () => {
    const rowsToSubmit = getRowsToSubmit();
    if (rowsToSubmit.length === 0) {
      enqueueSnackbar(t("Please enter at least one counter value"), ERROR_SNACKBAR);
      return;
    }
    if (rowsToSubmit.some((row) => !row.equipmentId || !row.kind)) {
      enqueueSnackbar(t("Equipment is required to add counter"), ERROR_SNACKBAR);
      return;
    }

    if (canEnterEveryCounterValue) {
      const warnings: LowCounterValueWarning[] = rowsToSubmit
        .map((row): LowCounterValueWarning | null => {
          const enteredValue = parseCounterNumericValue(counterValues[row.key]);
          const latestValue = parseCounterNumericValue(row.previousValue);
          if (enteredValue === null || latestValue === null || enteredValue >= latestValue) {
            return null;
          }
          return {
            key: row.key,
            label: getCounterKindLabel(row.kind),
            enteredValue,
            latestValue,
            latestDate: row.previousDate,
          };
        })
        .filter((warning): warning is LowCounterValueWarning => warning !== null);

      if (warnings.length > 0) {
        setLowValueWarnings(warnings);
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performBulkSaveCounters(rowsToSubmit);
  };

  const handleConfirmLowValueBulkSave = () => {
    void performBulkSaveCounters(getRowsToSubmit());
  };

  const handleVerify = async (counterIds: string[]) => {
    if (counterIds.length === 0) {
      return;
    }

    setVerifying(true);
    try {
      await axios.post(EQUIPMENT_COUNTERS_VERIFY_ENDPOINT, { counter_ids: counterIds });
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/refresh-calculation/`);
      enqueueSnackbar(t("Counters marked as verified"), SUCCESS_SNACKBAR);
      setDirty(true);
      await fetchSettlement();
    } catch (err) {
      enqueueSnackbar(t("Failed to verify counters"), ERROR_SNACKBAR);
    } finally {
      setVerifying(false);
    }
  };

  const handleConfirm = async () => {
    if (!saleDate) {
      enqueueSnackbar(t("Sale date is required"), ERROR_SNACKBAR);
      return;
    }

    setConfirmSubmitting(true);
    try {
      await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlementId}/`, {
        status: "C",
        sale_date: saleDate,
        total_overridden: totalOverridden || null,
      });
      enqueueSnackbar(t("Settlement confirmed"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("settlementConfirmed"));
      setOpened(false);
    } catch (err) {
      enqueueSnackbar(t("Failed to confirm settlement"), ERROR_SNACKBAR);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {loading ? <CustomCircularProgress /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && settlement ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2">
              <strong>{t("Client")}:</strong> {settlement.contract_term?.client_name || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("Contract number")}:</strong>{" "}
              {settlement.contract_term?.contract_info?.number || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("Equipment")}:</strong>{" "}
              {equipments.map((equipment) => equipment.full_name).filter(Boolean).join(", ") || "-"}
            </Typography>
          </Box>

          {previousCounterRows.length > 0 ? (
            <Paper variant="outlined" className={creatorStyles.previousCounterSection}>
              <CounterDetailsTable
                data={previousCounterRows}
                showEditButton
                onEdit={(counterId) => setEditingCounterId(counterId)}
              />
            </Paper>
          ) : null}

          {canManageCounters &&
          (allInputRows.length > 0 || equipmentsNeedingScanButton.length > 0) ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2">{t("Counter values needed")}</Typography>

              {allInputRows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <TextField
                    size="small"
                    label={rowFieldLabel(row)}
                    value={formatCounterInputValue(counterValues[row.key] || "")}
                    onChange={(event) => {
                      const normalizedValue = normalizeCounterInputValue(event.target.value);
                      if (normalizedValue === null) {
                        return;
                      }

                      setCounterValues((prev) => ({ ...prev, [row.key]: normalizedValue }));
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    sx={{ flex: 1, minWidth: { xs: "100%", sm: 220 } }}
                  />
                  {row.removable ? (
                    <Button size="small" color="inherit" onClick={() => removeAdHocRow(row.key)}>
                      {t("Remove")}
                    </Button>
                  ) : null}
                </Box>
              ))}

              {equipmentsNeedingScanButton.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {equipmentsNeedingScanButton.map((equipment) => (
                    <Button
                      key={equipment.id}
                      size="small"
                      variant="text"
                      startIcon={<Add fontSize="small" />}
                      onClick={() => addScanRow(equipment.id)}
                    >
                      {hasMultipleEquipments
                        ? t("Add Scan counter for {{equipment}}", {
                            equipment: equipment.full_name,
                          })
                        : t("Add Scan counter")}
                    </Button>
                  ))}
                </Box>
              ) : null}

              {allInputRows.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={bulkSubmitting}
                    onClick={handleBulkSaveCounters}
                  >
                    {bulkSubmitting ? t("Processing...") : t("Save")}
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {unverifiedRows.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Paper variant="outlined" className={creatorStyles.counterDetailsSection}>
                <CounterDetailsTable
                  title={t("Counters to verify")}
                  data={unverifiedRows}
                  showEditButton
                  onEdit={(counterId) => setEditingCounterId(counterId)}
                />
              </Paper>
              {unverifiedRows.length > 1 ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={verifying}
                  onClick={() => handleVerify(unverifiedRows.map((row) => row.id))}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {verifying ? t("Processing...") : t("Verify all")}
                </Button>
              ) : null}
            </Box>
          ) : null}

          {readyToConfirm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2">{t("Confirm settlement")}</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <CustomDatePicker
                  label={t("Sale date")}
                  value={saleDate ? dayjs(saleDate) : null}
                  onChange={(value: Dayjs | null) =>
                    setSaleDate(value ? value.format("YYYY-MM-DD") : "")
                  }
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
                />
                <TextField
                  size="small"
                  label={t("Total Overridden")}
                  value={totalOverridden}
                  onChange={(event) => setTotalOverridden(event.target.value)}
                  sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                disabled={confirmSubmitting}
                onClick={handleConfirm}
              >
                {confirmSubmitting ? t("Processing...") : t("Confirm")}
              </Button>
            </Box>
          ) : null}

          {(!canManageCounters || allInputRows.length === 0) &&
          unverifiedRows.length === 0 &&
          !readyToConfirm ? (
            <Alert severity="info">{t("No action needed")}</Alert>
          ) : null}
        </>
      ) : null}
    </Box>
  );

  const titleContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {t("Manage settlement")}
      </Box>
      {settlement ? (
        <Chip
          label={settlement.status_display}
          color={SETTLEMENT_STATUS_TO_COLOR[settlement.status] || "default"}
          size="small"
          sx={{ flexShrink: 0 }}
        />
      ) : null}
    </Box>
  );

  return (
    <>
      <CustomModal
        opened={opened}
        setOpened={handleModalSetOpened}
        title={titleContent}
        width="min(560px, 92vw)"
        content={content}
      />

      <LowCounterValueConfirmModal
        opened={lowValueConfirmOpen}
        setOpened={setLowValueConfirmOpen}
        warnings={lowValueWarnings}
        onConfirm={handleConfirmLowValueBulkSave}
      />

      <CustomModal
        opened={Boolean(editingCounterId)}
        setOpened={(value) => {
          if (!value) {
            setEditingCounterId(null);
          }
        }}
        title={t("Edit counter value")}
        width="min(480px, 92vw)"
        content={
          editingCounterId ? (
            <EquipmentCounterForm
              id={editingCounterId}
              hiddenFields={["equipment_id", "job", "kind"]}
              onSuccess={() => {
                setEditingCounterId(null);
                setDirty(true);
                fetchSettlement();
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
};
